import { useFetchEQLFields } from '@modules/common/hooks';
import {
  getEqlFieldFromFieldStringValue,
  mapToDateTimeRange,
} from '@modules/common/utils';
import { FormulaQueryProperty } from '@modules/quantify/store/types';
import { getQuantifyAnalysisURL } from '@modules/quantify/utils';
import { GroupByResponse, QuantifyVisualisationType } from 'generated/graphql';
import { compressToEncodedURIComponent } from 'lz-string';
import { FieldNames } from 'v2/core/entities/fields';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';

export const getFieldName = (groupBy?: GroupByResponse): string => {
  if (!groupBy) {
    return FieldNames.FeedbackRecords;
  }

  if (groupBy.entity || groupBy.entityCategory) {
    return FieldNames.Entity;
  }

  if (groupBy.reason) {
    return FieldNames.Reason;
  }

  if (groupBy.language) {
    return FieldNames.Language;
  }

  if (groupBy.source) {
    return FieldNames.Source;
  }

  if (groupBy.entitySentiment) {
    return FieldNames.EntitySentiment;
  }

  if (groupBy.recordSentiment) {
    return FieldNames.ReviewSentiment;
  }

  if (groupBy.metadata) {
    return groupBy.metadata.keys?.join() ?? '';
  }

  return FieldNames.FeedbackRecords;
};

export const useGetQuantifyLink = () => {
  const { fields: eqlFields, isLoading: isLoadingEQLFields } =
    useFetchEQLFields({
      shouldExcludeNonGroupByFields: true,
      shouldIncludeFeedbackRecordsFilter: true,
    });

  const {
    tenant: { timezone },
  } = useGetUser();

  const { data: sourceDetailsMap, status } = useGetSourceDetailsMap();

  const getQuantifyLink = (eql: any) => {
    if (isLoadingEQLFields || IsLoading(status)) {
      return null;
    }

    let eqlString = '';
    let whereClause = EmptyWhereClause;

    const groupByField = getFieldName(eql?.properties.groupBy);

    const selectedGroupByOption =
      eqlFields.find(
        (eqlField) =>
          eqlField.field === getEqlFieldFromFieldStringValue(groupByField),
      ) ?? null;

    const formulaID = eql?.properties.formula?.eqlFunction
      ? (eql.properties.formula
          .eqlFunction as FormulaQueryProperty['eqlFunction'])
      : EQLFunctionName.Count;

    let duration = { ...eql.properties.duration };
    if (eql?.properties.duration) {
      if (duration.startAfter) {
        duration.startAfter = duration.startAfter * 1000;
      }
      if (duration.endBefore) {
        duration.endBefore = duration.endBefore * 1000;
      }

      duration = mapToDateTimeRange(duration);
    }

    if (eql?.properties.whereClause && sourceDetailsMap) {
      eqlString = eql.properties.whereClause.startsWith('select')
        ? eql.properties.whereClause
        : `select aggregates where ${eql.properties.whereClause}`;

      whereClause =
        parseEQL(eqlString, sourceDetailsMap, timezone).whereClause ??
        EmptyWhereClause;
    }

    const searchParams = getQuantifyAnalysisURL({
      groupByValue: selectedGroupByOption?.field,
      duration,
      whereClause,
      formulaQuery: {
        eqlFunction: formulaID,
      },
      isCoOccurring: eql?.properties.isCoOccurring ?? false,
      shouldShowCategories:
        !!eql?.properties.groupBy?.entity &&
        !!eql?.properties.groupBy?.entityCategory,
      visualisationType: eql?.properties.aggregatePeriod
        ? QuantifyVisualisationType.Trend
        : QuantifyVisualisationType.Bar,
    });

    return compressToEncodedURIComponent(searchParams);
  };

  return {
    getQuantifyLink,
    isLoading: isLoadingEQLFields || IsLoading(status),
  };
};
